import PropTypes from 'prop-types'
import { useMemo } from 'react'
import { LuFilter, LuMoveDown, LuMoveUp } from 'react-icons/lu'
import {
  useFilters,
  useGlobalFilter,
  usePagination,
  useSortBy,
  useTable
} from 'react-table'
import TableActionBar from './TableActionBar'
import TableFooter from './TableFooter'

export default function Table({
  tableName,
  columns,
  columnsToWrap,
  createNewItemURL,
  data,
  defaultSort,
  hiddenColumns,
  allowImportExcel,
  uploadExcelURL,
  handleRefresh,
  handleAnalyzeExcel
}) {
  // Filtro personalizado para rango de fechas, código basado en este ejemplo: https://github.com/TanStack/table/discussions/2453
  function dateBetweenFilterFn(rows, id, filterValues) {
    let startDate = null
    let endDate = null

    // Convertir fecha de inicio en un objeto 'Date'
    if (
      typeof filterValues[0] === 'string' &&
      filterValues[0] !== '' &&
      filterValues[0] !== null &&
      filterValues[0] !== undefined
    ) {
      const parts = filterValues[0].split('-')

      if (parts.length === 3) {
        let [year, month, day] = parts.map((num) => parseInt(num, 10))
        year = year < 100 ? 2000 + year : year
        startDate = new Date(Date.UTC(year, month - 1, day, 0, 0, 0))
      }
    }

    // Convertir fecha de fin en un objeto 'Date'
    if (
      typeof filterValues[1] === 'string' &&
      filterValues[1] !== '' &&
      filterValues[1] !== null &&
      filterValues[1] !== undefined
    ) {
      const parts = filterValues[1].split('-')

      if (parts.length === 3) {
        let [year, month, day] = parts.map((num) => parseInt(num, 10))
        year = year < 100 ? 2000 + year : year
        endDate = new Date(Date.UTC(year, month - 1, day, 0, 0, 0))
      }
    }

    // Filtrar filas por fecha dentro del rango
    return rows.filter((r) => {
      let rowDate = null

      // Convertir fecha de la fila en un objeto 'Date'
      if (
        typeof r.values[id] === 'string' &&
        r.values[id] !== '' &&
        r.values[id] !== null &&
        r.values[id] !== undefined
      ) {
        const parts = r.values[id].split('/')

        if (parts.length === 3) {
          let [day, month, year] = parts.map((num) => parseInt(num, 10))
          year = year < 100 ? 2000 + year : year
          rowDate = new Date(Date.UTC(year, month - 1, day, 0, 0, 0))
        }
      }

      // Filtrar según las fechas de inicio y fin
      if (rowDate !== null) {
        if (filterValues.length === 0 || (!startDate && !endDate)) {
          return true // No se aplican filtros, devolver todas las filas
        } else if (startDate && !endDate) {
          return rowDate >= startDate // Solo fecha de inicio, filtrar por fechas posteriores o iguales
        } else if (!startDate && endDate) {
          return rowDate <= endDate // Solo fecha de fin, filtrar por fechas anteriores o iguales
        } else {
          return rowDate >= startDate && rowDate <= endDate // Ambas fechas existen, filtrar entre ellas
        }
      }

      return false
    })
  }

  dateBetweenFilterFn.autoRemove = (val) => !val

  // Definir tipos de filtros personalizados
  const filterTypes = useMemo(
    () => ({
      dateBetween: dateBetweenFilterFn
    }),
    []
  )

  // Configuración de la tabla con react-table
  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    prepareRow,
    page, // Filas de la página actual
    setAllFilters,
    rows, // Filas filtradas
    canPreviousPage,
    canNextPage,
    pageOptions,
    gotoPage,
    nextPage,
    previousPage,
    setPageSize,
    state: { pageIndex, pageSize } // Información de paginación
  } = useTable(
    {
      columns,
      data,
      filterTypes,
      initialState: {
        pageSize: 25, // Número de filas por página
        sortBy: defaultSort ? [defaultSort] : [], // Ordenación predeterminada, si existe
        hiddenColumns: hiddenColumns?.length ? hiddenColumns : [] // Columnas ocultas en la tabla, pero visibles en la exportación
      }
    },
    useGlobalFilter,
    useFilters,
    useSortBy,
    usePagination
  )

  return (
    <>
      <div className="w-full mx-auto">
        {/* Barra de acciones para la tabla */}
        <TableActionBar
          tableName={tableName}
          columns={columns}
          columnsToWrap={columnsToWrap?.length ? columnsToWrap : []}
          rows={rows}
          createNewItemURL={createNewItemURL}
          allowImportExcel={allowImportExcel}
          uploadExcelURL={uploadExcelURL}
          handleRefresh={handleRefresh}
          handleAnalyzeExcel={handleAnalyzeExcel}
          setAllFilters={setAllFilters}
        />

        {/* Tabla */}
        <div className="flex flex-col mt-4">
          <div className="overflow-x-auto sm:-mx-6 lg:-mx-8">
            <div className="inline-block min-w-full py-2 align-middle md:px-6 lg:px-8">
              <div className="overflow-hidden border border-gray-200 dark:border-gray-700 md:rounded-lg">
                <table
                  {...getTableProps()}
                  className="min-w-full divide-y divide-gray-200 dark:divide-gray-700"
                >
                  {/* Cabecera */}
                  <thead className="bg-gray-50 dark:bg-gray-800">
                    {headerGroups.map((headerGroup, index) => (
                      <tr key={index} {...headerGroup.getHeaderGroupProps()}>
                        {headerGroup.headers.map((header, index) => {
                          // La siguiente línea de código sirve para solucionar el siguiente error
                          //  Warning: A props object containing a "key" prop is being spread into JSX:
                          //    let props = {key: someKey, role: ..., onClick: ..., className: ..., children: ...};
                          //    <tr {...props} />
                          //  React keys must be passed directly to JSX without using spread:
                          //    let props = {role: ..., onClick: ..., className: ..., children: ...};
                          //    <tr key={someKey} {...props} />
                          //  Error Component Stack
                          const { key, ...headerProps } = header.getHeaderProps(
                            header.getSortByToggleProps({
                              title: header.TitleDescription
                            })
                          )

                          return (
                            <th
                              key={key}
                              scope="col"
                              className="py-3.5 px-4 text-sm font-normal text-left rtl:text-right text-gray-500 dark:text-gray-400"
                              {...headerProps}
                            >
                              <div
                                title={header?.title}
                                className="flex flex-col justify-between h-full space-y-2"
                              >
                                <div
                                  className={`${header?.Filter ? 'flex-row justify-start items-center text-left space-x-0.5' : 'justify-center items-center h-full'} flex w-full text-sm select-none`}
                                >
                                  {header.Filter ? (
                                    <span className="flex items-center justify-center space-x-1 overflow-hidden whitespace-nowrap text-ellipsis">
                                      <LuFilter size={15} />
                                      <span>{header.render('Header')}</span>
                                    </span>
                                  ) : (
                                    <span className="overflow-hidden whitespace-nowrap text-ellipsis">
                                      {header.render('Header')}
                                    </span>
                                  )}

                                  <div>
                                    {header.isSorted ? (
                                      header.isSortedDesc ? (
                                        <LuMoveDown size={15} />
                                      ) : (
                                        <LuMoveUp size={15} />
                                      )
                                    ) : (
                                      ''
                                    )}
                                  </div>
                                </div>

                                <div>
                                  {header.Filter
                                    ? header.render('Filter')
                                    : null}
                                </div>
                              </div>
                            </th>
                          )
                        })}
                      </tr>
                    ))}
                  </thead>

                  {/* Cuerpo */}
                  {rows?.length > 0 ? (
                    <tbody
                      className="bg-white divide-y divide-gray-200 dark:divide-gray-700 dark:bg-gray-900"
                      {...getTableBodyProps()}
                    >
                      {page.map((row, index) => {
                        prepareRow(row)

                        // La siguiente línea de código sirve para solucionar el siguiente error
                        //  Warning: A props object containing a "key" prop is being spread into JSX:
                        //    let props = {key: someKey, role: ..., onClick: ..., className: ..., children: ...};
                        //    <tr {...props} />
                        //  React keys must be passed directly to JSX without using spread:
                        //    let props = {role: ..., onClick: ..., className: ..., children: ...};
                        //    <tr key={someKey} {...props} />
                        //  Error Component Stack
                        const { key, ...rowProps } = row.getRowProps()

                        return (
                          <>
                            <tr key={key} {...rowProps}>
                              {row.cells.map((cell, index) => {
                                return (
                                  <td
                                    title={cell?.column?.Header}
                                    key={index}
                                    {...cell.getCellProps()}
                                    className="p-4 text-sm font-medium text-gray-700 whitespace-nowrap"
                                  >
                                    {cell.render('Cell')}
                                  </td>
                                )
                              })}
                            </tr>
                          </>
                        )
                      })}
                    </tbody>
                  ) : (
                    <tbody
                      className="bg-white divide-y divide-gray-200 dark:divide-gray-700 dark:bg-gray-900"
                      {...getTableBodyProps()}
                    >
                      <tr>
                        <td
                          className="p-4 text-sm font-medium text-gray-700 whitespace-nowrap"
                          colSpan={columns?.length}
                        >
                          No se han encontrado registros
                        </td>
                      </tr>
                    </tbody>
                  )}
                </table>
              </div>
            </div>
          </div>
        </div>

        {/* Pie de tabla */}
        <div className="mt-4 sm:flex sm:items-center sm:justify-between">
          <TableFooter
            previousPage={previousPage}
            canPreviousPage={canPreviousPage}
            pageIndex={pageIndex}
            pageOptions={pageOptions}
            nextPage={nextPage}
            canNextPage={canNextPage}
            gotoPage={gotoPage}
            rows={rows}
            pageSize={pageSize}
            setPageSize={setPageSize}
          />
        </div>
      </div>
    </>
  )
}

Table.propTypes = {
  tableName: PropTypes.string.isRequired,
  columns: PropTypes.array.isRequired,
  columnsToWrap: PropTypes.array.isRequired,
  createNewItemURL: PropTypes.string.isRequired,
  data: PropTypes.array,
  defaultSort: PropTypes.any,
  hiddenColumns: PropTypes.array,
  allowImportExcel: PropTypes.bool.isRequired,
  uploadExcelURL: PropTypes.string,
  handleRefresh: PropTypes.func,
  handleAnalyzeExcel: PropTypes.func
}
